import React, { useCallback, useState } from 'react';
import { ArrowUp, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import useStore from './store';
import { useMentions } from './hooks/useMentions';
import MentionDropdown from './MentionDropdown';
import { ModelSelector } from './ModelSelector';

interface ChatInputProps {
    onSendMessage: (message: string | FormData) => void;
    isReady: boolean;
    isStreaming?: boolean;
    placeholder?: string;
    disabled?: boolean;
    variant?: 'default' | 'ghost';
    className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    onSendMessage,
    isReady,
    isStreaming = false,
    placeholder = "Ask ORA anything... Use @ to mention",
    disabled = false,
    variant = 'default',
    className = ''
}) => {
    const inputMessage = useStore((s) => s.inputMessage);
    const setInputMessage = useStore((s) => s.setInputMessage);
    const mentions = useStore((s) => s.mentions);
    const setMentions = useStore((s) => s.setMentions);
    const chatInputRef = useStore((s) => s.chatInputRef) as React.RefObject<HTMLTextAreaElement>;
    const [isFocused, setIsFocused] = useState(false);

    const { mentionState, insertMention, handleKeyDown: handleMentionKeyDown, closeMentions } = useMentions({
        textAreaRef: chatInputRef,
        inputMessage,
        setInputMessage,
    });

    const handleSendMessage = useCallback(() => {
        const messageToSend = inputMessage.trim();
        if (!messageToSend || isStreaming || disabled) return;
        if (!isReady) {
            toast.info('Agent is still connecting — try again in a moment.');
            return;
        }

        if (mentions.length > 0) {
            let enrichedMessage = messageToSend;
            mentions.forEach(mention => {
                const mentionPattern = new RegExp(`@${mention.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
                const replacement = `@${mention.label} [external_id:${mention.external_id}]`;
                enrichedMessage = enrichedMessage.replace(mentionPattern, replacement);
            });

            const formData = new FormData();
            formData.append('message', enrichedMessage);
            const mentionContext = mentions.map(m => ({
                label: m.label,
                external_id: m.external_id,
                type: m.type,
                domain: m.domain
            }));
            formData.append('mentions', JSON.stringify(mentionContext));
            onSendMessage(formData);
        } else {
            onSendMessage(messageToSend);
        }

        setInputMessage('');
        setMentions([]);
        closeMentions();
    }, [inputMessage, isReady, isStreaming, disabled, onSendMessage, closeMentions, setInputMessage, mentions, setMentions]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        const mentionHandled = handleMentionKeyDown(e);
        if (mentionHandled) return;

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const typingDisabled = disabled || isStreaming;
    const sendDisabled = typingDisabled;
    const hasInput = inputMessage.trim().length > 0;

    return (
        <div className={cn(variant === 'default' ? "p-4 bg-background" : "w-full bg-transparent", className)}>
            <div className="max-w-4xl mx-auto">
                {/* Mention Dropdown */}
                <MentionDropdown
                    isOpen={mentionState.isOpen}
                    options={mentionState.options}
                    selectedIndex={mentionState.selectedIndex}
                    loading={mentionState.loading}
                    onSelect={insertMention}
                    textAreaRef={chatInputRef}
                />

                <div
                    className={cn(
                        "relative bg-card rounded-lg border transition-all duration-150",
                        isFocused ? "border-primary" : "border-border-chip"
                    )}
                >
                    <div className="flex items-center min-h-[44px]">
                        {/* Model selector */}
                        <div className="pl-2 flex-shrink-0">
                            <ModelSelector />
                        </div>

                        {/* Input */}
                        <div className="relative flex-1">
                            <textarea
                                ref={chatInputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyDown={handleKeyPress}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                placeholder={placeholder}
                                disabled={typingDisabled}
                                rows={1}
                                className="relative z-10 w-full bg-transparent px-3 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50 resize-none !border-none max-h-32 overflow-y-auto"
                                style={{ caretColor: 'hsl(var(--primary))', minHeight: '28px' }}
                                onInput={(e) => {
                                    const target = e.target as HTMLTextAreaElement;
                                    target.style.height = 'auto';
                                    target.style.height = `${target.scrollHeight}px`;
                                }}
                            />
                        </div>

                        {/* Right buttons */}
                        <div className="flex items-center gap-1 pr-2">
                            <button
                                type="button"
                                className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={handleSendMessage}
                                disabled={sendDisabled || !hasInput}
                                className={cn(
                                    "w-7 h-7 rounded-[5px] flex items-center justify-center transition-all",
                                    hasInput && !sendDisabled
                                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                        : "bg-muted text-muted-foreground"
                                )}
                            >
                                <ArrowUp className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* AI disclaimer */}
                <div className="flex items-center justify-center mt-2 px-1">
                    <span className="text-[11px] text-muted-foreground">
                        AI-generated responses may not always be accurate
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ChatInput;
